import type { DualWriteEvent } from '../../../../../../src/shared/dual-write-types';
import { MissingFieldsDetailsSchema } from './types';
import type { MissingFieldEntry } from './types';
import { readStoredJson } from '../../../lib/readStoredJson';
import { z } from 'zod';

// ── Types ──────────────────────────────────────────────────────

export interface WarningRule {
  id: string;
  label: string;
  description: string;
  tooltip?: string;
  enabled: boolean;
  test: (evt: DualWriteEvent) => 'suppress' | 'pass';
}

export interface SuppressionDetail {
  count: number;
  /** blueprintKind -> count */
  kinds: Map<string, number>;
}

// ── Helpers ────────────────────────────────────────────────────

function getMissing(evt: DualWriteEvent): MissingFieldEntry[] {
  const parsed = MissingFieldsDetailsSchema.safeParse(evt.errorDetails);
  return parsed.success ? parsed.data.missing : [];
}

// ── Default Rules ──────────────────────────────────────────────

function getDefaultWarningRules(): WarningRule[] {
  return [
    {
      id: 'hide-source-gap-fields',
      label: 'Source gap fields',
      description: 'Hide warnings where the data was never in legacy either',
      tooltip: 'Suppresses if every missing field has legacyPresent=false. legacyPresent is set by the dual-write bridge: it checks whether each missing field exists in the legacy payload. false means legacy never had it either, so it\'s a gap in the source data — not a mapping bug.',
      enabled: true,
      test: (evt) => {
        const missing = getMissing(evt);
        if (missing.length === 0) return 'pass';
        return missing.every((m) => m.legacyPresent === false) ? 'suppress' : 'pass';
      },
    },
    {
      id: 'hide-field:account_name',
      label: 'account_name (async)',
      description: 'Pulled asynchronously, always missing from bridge payload',
      tooltip: 'Suppresses if every missing field is account_name. This field is fetched asynchronously after ingestion — it will be populated eventually, but is always missing at bridge time.',
      enabled: true,
      test: (evt) => {
        const missing = getMissing(evt);
        if (missing.length === 0) return 'pass';
        return missing.every((m) => m.field === 'account_name') ? 'suppress' : 'pass';
      },
    },
    {
      id: 'hide-source:card_slot',
      label: 'Card slot fields',
      description: 'Fields required by card display slots',
      tooltip: 'Suppresses if every missing field has source=card_slot. These are fields needed for card rendering (title, subtitle, account name slots) — cosmetic, not functional.',
      enabled: false,
      test: (evt) => {
        const missing = getMissing(evt);
        if (missing.length === 0) return 'pass';
        return missing.every((m) => m.source === 'card_slot') ? 'suppress' : 'pass';
      },
    },
    {
      id: 'hide-source:action_form',
      label: 'Action form fields',
      description: 'Fields required by action modal forms',
      tooltip: 'Suppresses if every missing field has source=action_form. These are fields needed by modal/review action forms — the action will still work but the form may show empty fields.',
      enabled: false,
      test: (evt) => {
        const missing = getMissing(evt);
        if (missing.length === 0) return 'pass';
        return missing.every((m) => m.source === 'action_form') ? 'suppress' : 'pass';
      },
    },
    {
      id: 'hide-source:action_handler',
      label: 'Action handler fields',
      description: 'Fields required by action handler config',
      tooltip: 'Suppresses if every missing field has source=action_handler. These are fields like recordIdField and referenceKeyField needed by action handler config — the action may fail without them.',
      enabled: false,
      test: (evt) => {
        const missing = getMissing(evt);
        if (missing.length === 0) return 'pass';
        return missing.every((m) => m.source === 'action_handler') ? 'suppress' : 'pass';
      },
    },
  ];
}

// ── Apply / Count ──────────────────────────────────────────────

export function applyWarningRules(events: DualWriteEvent[], rules: WarningRule[]): DualWriteEvent[] {
  const enabled = rules.filter((r) => r.enabled);
  if (enabled.length === 0) return events;
  return events.filter((evt) => enabled.every((rule) => rule.test(evt) === 'pass'));
}

export function countWarningSuppression(
  events: DualWriteEvent[],
  rules: WarningRule[],
): Map<string, SuppressionDetail> {
  const details = new Map<string, SuppressionDetail>();
  for (const rule of rules) {
    const detail: SuppressionDetail = { count: 0, kinds: new Map() };
    for (const evt of events) {
      if (rule.test(evt) === 'suppress') {
        detail.count++;
        const kind = evt.blueprintKind ?? 'unknown';
        detail.kinds.set(kind, (detail.kinds.get(kind) ?? 0) + 1);
      }
    }
    details.set(rule.id, detail);
  }
  return details;
}

// ── Persistence ────────────────────────────────────────────────

const STORAGE_KEY = 'dual-write-warning-rules';

const StoredRuleStatesSchema = z.record(z.string(), z.boolean());

function loadRuleStates(): Record<string, boolean> | null {
  try {
    return readStoredJson(localStorage, STORAGE_KEY, StoredRuleStatesSchema.nullable(), null);
  } catch {
    return null;
  }
}

export function initWarningRules(): WarningRule[] {
  const defaults = getDefaultWarningRules();
  const saved = loadRuleStates();
  if (!saved) return defaults;
  return defaults.map((r) => ({
    ...r,
    enabled: saved[r.id] ?? r.enabled,
  }));
}

export function saveWarningRules(rules: WarningRule[]): void {
  const states: Record<string, boolean> = {};
  for (const r of rules) states[r.id] = r.enabled;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(states));
}
